import { KPV_CC, KPV_BANK_NOTES } from '../engine/protocol';
import type { KpvStore } from '../state/store';

/**
 * WebMIDI input speaking the KPV's own control map: an actual KAOSS PAD KPV
 * (or anything sending its CCs) drives this app exactly like the hardware.
 *
 *   CC12/13 + CC92 = touch 1 X/Y + on/off      CC93 = level fader
 *   CC14/15 + CC96 = touch 2 X/Y + on/off      CC94 = FX depth
 *   CC95 = hold switch                          notes 60–63 = banks A–D
 *
 * MIDI-learn / custom maps are a later phase — this is the fixed default map.
 */
export class MidiManager {
  private access: MIDIAccess | null = null;
  inputs: string[] = [];
  channel: number | null = null; // null = omni

  /** MIDI clock follow (0xF8 = 24 ppqn). On by default; tap/manual overrides pause it. */
  clockFollow = true;
  private clockTimes: number[] = [];
  private lastClockAt = 0;

  // Cache incoming coords so on/off arrives in any order.
  private x1 = 0.5;
  private y1 = 0.5;
  private x2 = 0.5;
  private y2 = 0.5;
  private t1 = false;
  private t2 = false;

  constructor(private store: KpvStore) {}

  async init(): Promise<boolean> {
    if (!('requestMIDIAccess' in navigator)) return false;
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
    } catch {
      return false;
    }
    const attach = (): void => {
      this.inputs = [];
      this.access!.inputs.forEach((input) => {
        this.inputs.push(input.name ?? input.id);
        input.onmidimessage = (e: MIDIMessageEvent) => this.onMessage(e);
      });
    };
    attach();
    this.access.onstatechange = attach;
    return true;
  }

  private onMessage(e: MIDIMessageEvent): void {
    const data = e.data;
    if (!data || data.length < 1) return;
    const status = data[0]!;

    // System realtime: clock follow (any channel filter doesn't apply).
    if (status === 0xf8) {
      this.onClockTick(e.timeStamp);
      return;
    }
    if (status === 0xfa || status === 0xfc) {
      this.clockTimes = []; // start/stop resets the estimator
      return;
    }

    if (data.length < 2) return;
    const kind = status & 0xf0;
    const ch = status & 0x0f;
    if (this.channel !== null && ch !== this.channel) return;

    if (kind === 0xb0 && data.length >= 3) {
      const cc = data[1]!;
      const v = data[2]! / 127;
      switch (cc) {
        case KPV_CC.touch1X:
          this.x1 = v;
          if (this.t1) this.store.pad1(true, this.x1, this.y1);
          break;
        case KPV_CC.touch1Y:
          this.y1 = v;
          if (this.t1) this.store.pad1(true, this.x1, this.y1);
          break;
        case KPV_CC.touch1On:
          this.t1 = v >= 0.5;
          this.store.pad1(this.t1, this.x1, this.y1);
          break;
        case KPV_CC.touch2X:
          this.x2 = v;
          if (this.t2) this.store.pad2(true, this.x2, this.y2);
          break;
        case KPV_CC.touch2Y:
          this.y2 = v;
          if (this.t2) this.store.pad2(true, this.x2, this.y2);
          break;
        case KPV_CC.touch2On:
          this.t2 = v >= 0.5;
          this.store.pad2(this.t2, this.x2, this.y2);
          break;
        case KPV_CC.fxDepth:
          this.store.setFxDepth(v);
          break;
        case KPV_CC.slider:
          this.store.engine.setOutputGain(v * 1.5);
          break;
        case KPV_CC.holdSw:
          this.store.setHold(v >= 0.5);
          break;
      }
      return;
    }

    if ((kind === 0x90 || kind === 0x80) && data.length >= 3) {
      const note = data[1]!;
      const on = kind === 0x90 && data[2]! > 0;
      const bank = (KPV_BANK_NOTES as readonly number[]).indexOf(note);
      if (bank >= 0 && on) this.store.bankPress(bank);
    }
  }

  /**
   * 24 ticks per quarter note. Median over the last 48 ticks (2 beats) gives
   * a jitter-tolerant BPM; only push to the store when it moves > 0.3 BPM so
   * we don't spam transport updates.
   */
  private onClockTick(t: number): void {
    if (!this.clockFollow) return;
    if (this.lastClockAt && t - this.lastClockAt > 500) this.clockTimes = []; // stream resumed
    this.lastClockAt = t;
    this.clockTimes.push(t);
    if (this.clockTimes.length > 49) this.clockTimes.shift();
    if (this.clockTimes.length < 25) return;
    const intervals: number[] = [];
    for (let i = 1; i < this.clockTimes.length; i++) {
      intervals.push(this.clockTimes[i]! - this.clockTimes[i - 1]!);
    }
    intervals.sort((a, b) => a - b);
    const median = intervals[Math.floor(intervals.length / 2)]!;
    if (median <= 0) return;
    const bpm = 60000 / (median * 24);
    if (bpm >= 20 && bpm <= 300 && Math.abs(bpm - this.store.bpm) > 0.3) {
      this.store.setBpm(bpm);
    }
  }
}
